import { computed, ref } from 'vue'
import { createInjectionState, toReactive, useLocalStorage } from '@vueuse/core'
import {
  useOrderByFilter,
  usePagination,
  useToQueryString,
  ToQueryStringFilters,
  recordActions,
} from '@/core'
import type { TReception } from '../../../types'
import { useModuleQuery } from '../query/useModuleQuery'
import { Pagination, TRecordAction } from '@/core/types'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from 'vue-auth3'
import { TUser } from '@/modules/auth'

const [useProvideModule, useModule] = createInjectionState(() => {
  const router = useRouter()
  const route = useRoute()
  const auth = useAuth()
  const modelId = ref<number>()

  const lsFilters = useLocalStorage<ToQueryStringFilters | null>(
    `${route.name?.toString()}_${auth.user<TUser>()?.id}`,
    {},
  )

  const rows = ref<number[]>([])
  const form = ref<TReception>({})
  const filters = ref<ToQueryStringFilters>({
    param: lsFilters.value?.param ?? '',
    id: lsFilters.value?.id ?? [],
    purchase_order_id: lsFilters.value?.purchase_order_id ?? [],
    status_id: lsFilters.value?.status_id ?? [],
    dates: {
      created_at: [],
      ...lsFilters.value?.dates,
    },
    order_by: lsFilters.value?.order_by ?? 'created_at,desc',
  })

  const moduleQueryParams = useToQueryString(toReactive(filters), {
    searchColumns: ['id'],
    persistFilters: true,
    persistKey: `${route.name?.toString()}_${auth.user<TUser>()?.id}`,
  })

  const paginationModule = usePagination()

  const moduleQuery = useModuleQuery({
    currentPage: paginationModule.currentPage,
    modelId: computed(() => Number(modelId.value)),
    filters: {
      loadRecords: moduleQueryParams.queryParams,
    },
    enableQuery: {
      loadRecords: computed(() => !!moduleQueryParams.queryParams.value),
      loadRecord: false,
      loadHistoryActions: computed(
        () => !!modelId.value && !isNaN(Number(modelId.value)),
      ),
    },
    handlers: {
      onDataChange: (pagination: Pagination) => {
        paginationModule.setPagination(pagination)
      },
    },
  })

  const { orderByOptions } = useOrderByFilter({
    columns: ['created_at', 'id'],
  })

  const onAction = async (action: TRecordAction) => {
    await moduleQuery.actionRecord.mutateAsync(action)
    if (action.action === recordActions.applied) {
      redirectToApply(action.id)
    }
  }

  const onRedirectToEdit = (order: TReception) => {
    router.push({
      name: 'purchaseOrdersDetail',
      params: {
        id: order.id,
      },
    })
  }

  const redirectToApply = (id: number) => {
    router.replace({
      name: 'receptions',
      query: {
        reception: id,
        action: 'apply',
        redirect: 'receptionsReceipts',
      },
    })
  }

  const onHistoryActions = (id: number) => {
    modelId.value = id
  }

  const tableData = computed(() => moduleQuery.loadRecords.data?.value?.data)
  const history = computed(() => moduleQuery.loadHistoryActions?.data?.value)

  const isLoading = computed(() => moduleQuery.loadRecords.isLoading.value)
  const isLoadingHistory = computed(
    () => moduleQuery.loadHistoryActions.isLoading.value,
  )

  return {
    tableData,
    filters,
    orderByOptions,
    rows,
    form,
    paginationModule: toReactive(paginationModule),
    isLoading,
    history,
    isLoadingHistory,
    onHistoryActions,
    onAction,
    onRedirectToEdit,
  }
})

export { useProvideModule, useModule }
